/**
 * Single AI provider status card with auth controls.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X, Key, Terminal, Trash2, RefreshCw, Download } from 'lucide-react';
import {
  updateProvider,
  storeProviderCredentials,
  deleteProviderCredentials,
  validateProvider,
  importProviderCredentials,
} from '@/lib/api';

interface ProviderData {
  id: string;
  provider: string;
  display_name: string;
  auth_status: 'none' | 'authenticated' | 'expired' | 'error';
  auth_method: string | null;
  enabled: boolean;
  last_validated_at: string | null;
  last_used_at: string | null;
  error_message: string | null;
  credential_keys: string[];
}

interface ProviderCardProps {
  provider: ProviderData;
  onOpenTerminal?: (command: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  authenticated: 'bg-success',
  expired: 'bg-warning',
  error: 'bg-error',
  none: 'bg-text-tertiary',
};

const AUTH_COMMANDS: Record<string, string> = {
  claude: 'claude auth login',
  codex: 'codex auth login',
};

const KEY_FIELDS: Record<string, { label: string; envVar: string }[]> = {
  claude: [{ label: 'Anthropic API Key', envVar: 'ANTHROPIC_API_KEY' }],
  codex: [{ label: 'OpenAI API Key', envVar: 'OPENAI_API_KEY' }],
  gemini: [{ label: 'Gemini API Key', envVar: 'GEMINI_API_KEY' }],
};

function formatTimestamp(ts: string | null): string {
  if (!ts) return 'Never';
  const d = new Date(ts);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60_000) return 'Just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ProviderCard({ provider, onOpenTerminal }: ProviderCardProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [apiKey, setApiKey] = useState('');

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['providers'] });
  };

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => updateProvider(provider.provider, { enabled }),
    onSuccess: invalidate,
  });

  const storeMutation = useMutation({
    mutationFn: (creds: { method: string; credentials: Record<string, string> }) =>
      storeProviderCredentials(provider.provider, creds.method, creds.credentials),
    onSuccess: () => {
      invalidate();
      setShowKeyForm(false);
      setApiKey('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteProviderCredentials(provider.provider),
    onSuccess: invalidate,
  });

  const importMutation = useMutation({
    mutationFn: () => importProviderCredentials(provider.provider),
    onSuccess: invalidate,
  });

  const validateMutation = useMutation({
    mutationFn: () => validateProvider(provider.provider),
    onSuccess: invalidate,
  });

  const fields = KEY_FIELDS[provider.provider] ?? [];
  const authCommand = AUTH_COMMANDS[provider.provider];

  function handleSubmitKey(e: React.FormEvent): void {
    e.preventDefault();
    if (!apiKey.trim() || fields.length === 0) return;
    const envVar = fields[0]?.envVar;
    if (!envVar) return;
    storeMutation.mutate({
      method: 'api_key',
      credentials: { [envVar]: apiKey.trim() },
    });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={`h-2.5 w-2.5 rounded-full ${STATUS_COLORS[provider.auth_status] ?? STATUS_COLORS.none}`}
            />
            <span className="text-sm">{provider.display_name}</span>
          </div>
          <Button
            variant={provider.enabled ? 'default' : 'outline'}
            size="sm"
            className="h-6 text-[10px] px-2"
            onClick={() => {
              toggleMutation.mutate(!provider.enabled);
            }}
            disabled={toggleMutation.isPending}
          >
            {provider.enabled ? 'Enabled' : 'Disabled'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Status info */}
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <Badge variant={provider.auth_status === 'authenticated' ? 'default' : 'secondary'}>
            {provider.auth_status}
          </Badge>
          {provider.auth_method && (
            <span className="text-text-tertiary">via {provider.auth_method}</span>
          )}
        </div>

        {provider.error_message && (
          <div className="text-xs text-error">{provider.error_message}</div>
        )}

        <div className="flex gap-4 text-xs text-text-tertiary">
          <span>Validated: {formatTimestamp(provider.last_validated_at)}</span>
          <span>Used: {formatTimestamp(provider.last_used_at)}</span>
        </div>

        {/* Stored keys */}
        {provider.credential_keys.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {provider.credential_keys.map(k => (
              <Badge key={k} variant="outline" className="text-[10px] font-mono">
                {k}
              </Badge>
            ))}
          </div>
        )}

        {/* API Key form */}
        {showKeyForm ? (
          <form onSubmit={handleSubmitKey} className="flex items-center gap-2">
            <Input
              type="password"
              value={apiKey}
              onChange={e => {
                setApiKey(e.target.value);
              }}
              placeholder={fields[0]?.label ?? 'API Key'}
              className="h-8 text-xs font-mono flex-1"
              autoFocus
            />
            <Button
              type="submit"
              size="sm"
              disabled={storeMutation.isPending || !apiKey.trim()}
              className="h-8"
            >
              {storeMutation.isPending ? '...' : <Check className="h-3 w-3" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowKeyForm(false);
                setApiKey('');
              }}
              className="h-8"
            >
              <X className="h-3 w-3" />
            </Button>
          </form>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {fields.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowKeyForm(true);
                }}
                className="h-7 text-xs"
              >
                <Key className="mr-1 h-3 w-3" />
                Enter API Key
              </Button>
            )}

            {provider.provider === 'claude' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  importMutation.mutate();
                }}
                disabled={importMutation.isPending}
                className="h-7 text-xs"
              >
                <Download
                  className={`mr-1 h-3 w-3 ${importMutation.isPending ? 'animate-pulse' : ''}`}
                />
                {importMutation.isPending ? 'Importing...' : 'Import from CLI'}
              </Button>
            )}

            {provider.provider === 'codex' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  importMutation.mutate();
                }}
                disabled={importMutation.isPending}
                className="h-7 text-xs"
              >
                <Download
                  className={`mr-1 h-3 w-3 ${importMutation.isPending ? 'animate-pulse' : ''}`}
                />
                {importMutation.isPending ? 'Importing...' : 'Import from CLI'}
              </Button>
            )}

            {authCommand && onOpenTerminal && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onOpenTerminal(authCommand);
                }}
                className="h-7 text-xs"
              >
                <Terminal className="mr-1 h-3 w-3" />
                Terminal Auth
              </Button>
            )}

            {provider.auth_status === 'authenticated' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  validateMutation.mutate();
                }}
                disabled={validateMutation.isPending}
                className="h-7 text-xs"
              >
                <RefreshCw
                  className={`mr-1 h-3 w-3 ${validateMutation.isPending ? 'animate-spin' : ''}`}
                />
                Re-validate
              </Button>
            )}

            {provider.credential_keys.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending}
                className="h-7 text-xs text-error hover:text-error"
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Remove
              </Button>
            )}
          </div>
        )}

        {storeMutation.isError && (
          <div className="text-xs text-error">
            {storeMutation.error instanceof Error
              ? storeMutation.error.message
              : 'Failed to store credentials'}
          </div>
        )}

        {importMutation.isError && (
          <div className="text-xs text-error">
            {importMutation.error instanceof Error
              ? importMutation.error.message
              : 'Failed to import credentials'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
